import { useFetchUserInfo, useSaveSetting } from '@/hooks/user-setting-hooks';
import {
  getBase64FromUploadFileList,
  getUploadFileListFromBase64,
  normFile,
} from '@/utils/file-util';
import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Tooltip,
  Upload,
  UploadFile,
} from 'antd';
import camelCase from 'lodash/camelCase';
import { useEffect } from 'react';
import { TimezoneList } from '../constants';
import { useValidateSubmittable } from '../hooks';

import { LanguageList } from '@/constants/common';
import { useTranslate } from '@/hooks/common-hooks';
import { useChangeLanguage } from '@/hooks/logic-hooks';
import { useLogout } from '@/hooks/login-hooks';
import { rsaPsw } from '@/utils';
import parentStyles from '../index.less';
import styles from './index.less';

const { Option } = Select;

type FieldType = {
  nickname?: string;
  language?: string;
  email?: string;
  color_schema?: string;
  timezone?: string;
  avatar?: string;
  password?: string;
  new_password?: string;
  confirm_password?: string;
};

const tailLayout = {
  wrapperCol: { offset: 20, span: 4 },
};

const BotzillaRegister = () => {
  const { logout } = useLogout();
  const { data: userInfo, loading } = useFetchUserInfo();
  const { saveSetting, loading: submitLoading } = useSaveSetting();
  const { form, submittable } = useValidateSubmittable();
  const { t } = useTranslate('setting');
  const changeLanguage = useChangeLanguage();

  const omit = (obj: any, ...props: any) => {
    const result = { ...obj };
    props.forEach(function (prop: any) {
      delete result[prop];
    });
    return result;
  };

  const onFinish = async (values: any) => {
    const avatar = await getBase64FromUploadFileList(values.avatar);
    const password = rsaPsw(values.password) as string;
    const new_password = rsaPsw(values.new_password) as string;
    saveSetting({
      ...omit(values, 'confirm_password'),
      avatar,
      password,
      new_password,
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    const fileList: UploadFile[] = getUploadFileListFromBase64(userInfo.avatar);
    form.setFieldsValue({ ...omit(userInfo, 'password'), avatar: fileList });
  }, [form, userInfo]);

  return (
    <Flex className={styles.settingWrapper}>
      <section className={styles.profileWrapper}>
        <p>
          <span className={styles.title}>{t('botzilla_register')}</span>
        </p>
        <p>
          <span className={styles.description}>
            {t('botzilla_register_description')}
          </span>
        </p>

        <Divider />
        <Spin spinning={loading}>
          <Form
            colon={false}
            name="basic"
            labelAlign={'left'}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            style={{ width: '100%' }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            autoComplete="off"
          >
            <Form.Item<FieldType>
              label={t('username')}
              name="nickname"
              rules={[
                {
                  required: true,
                  message: t('usernameMessage'),
                  whitespace: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Divider />
            <Form.Item<FieldType>
              label={t('initialPassword')}
              name="password"
              rules={[
                {
                  required: true,
                  message: t('currentPasswordMessage'),
                  whitespace: true,
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Divider />
            <Form.Item label={t('newPassword')} required>
              <Form.Item<FieldType>
                noStyle
                name="new_password"
                rules={[
                  {
                    required: true,
                    message: t('newPasswordMessage'),
                    whitespace: true,
                  },
                  {
                    type: 'string',
                    min: 8,
                    message: t('newPasswordDescription'),
                  },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Form.Item>
            <Form.Item<FieldType>
              label={t('confirmPassword')}
              name="confirm_password"
              dependencies={['new_password']}
              rules={[
                {
                  required: true,
                  message: t('confirmPasswordMessage'),
                  whitespace: true,
                },
                {
                  type: 'string',
                  min: 8,
                  message: t('newPasswordDescription'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t('confirmPasswordNonMatchMessage')),
                    );
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>

            <Divider />
            <Form.Item<FieldType>
              label={
                <div>
                  <Space>{t('photo')}</Space>
                  <div>{t('photoDescription')}</div>
                </div>
              }
              name="avatar"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload
                listType="picture-card"
                maxCount={1}
                accept="image/*"
                beforeUpload={() => {
                  return false;
                }}
                showUploadList={{
                  showPreviewIcon: false,
                  showRemoveIcon: true,
                }}
              >
                <button style={{ border: 0, background: 'none' }} type="button">
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>
                    {t('upload', { keyPrefix: 'common' })}
                  </div>
                </button>
              </Upload>
            </Form.Item>
            <Divider />
            <Form.Item<FieldType>
              hidden={true}
              label={t('colorSchema')}
              name="color_schema"
              rules={[{ required: true, message: t('colorSchemaMessage') }]}
            >
              <Select placeholder={t('colorSchemaPlaceholder')}>
                <Option value="Bright">{t('bright')}</Option>
                <Option value="Dark">{t('dark')}</Option>
              </Select>
            </Form.Item>
            {/* <Divider /> */}
            <Form.Item<FieldType>
              hidden={true}
              label={t('language', { keyPrefix: 'common' })}
              name="language"
              rules={[
                {
                  required: true,
                  message: t('languageMessage', { keyPrefix: 'common' }),
                },
              ]}
            >
              <Select
                placeholder={t('languagePlaceholder', { keyPrefix: 'common' })}
                onChange={changeLanguage}
              >
                {LanguageList.map((x) => (
                  <Option value={x} key={x}>
                    {t(camelCase(x), { keyPrefix: 'common' })}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {/* <Divider /> */}
            <Form.Item<FieldType>
              label={t('timezone')}
              name="timezone"
              rules={[{ required: true, message: t('timezoneMessage') }]}
            >
              <Select placeholder={t('timezonePlaceholder')} showSearch>
                {TimezoneList.map((x) => (
                  <Option value={x} key={x}>
                    {x}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Divider />
            <Form.Item label={t('email')}>
              <Form.Item<FieldType> name="email" noStyle>
                <Input disabled />
              </Form.Item>
              <p className={parentStyles.itemDescription}>
                {t('emailDescription')}
              </p>
            </Form.Item>
            <Form.Item
              {...tailLayout}
              shouldUpdate={(prevValues, curValues) =>
                prevValues.additional !== curValues.additional
              }
            >
              <Space>
                <Button htmlType="button" onClick={() => logout()}>
                  {t('cancel')}
                </Button>
                <Tooltip title={submittable ? '' : t('completeFormPlease')}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={!submittable}
                    loading={submitLoading}
                  >
                    {t('save', { keyPrefix: 'common' })}
                  </Button>
                </Tooltip>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </section>
    </Flex>
  );
};

export default BotzillaRegister;
